import React, { useEffect, useMemo, useState } from 'react';
import { apiGet, apiPost, apiPut, apiDelete } from '../../api/client';
import MediaPicker from '../../components/admin/MediaPicker';

const emptyReview = {
  id: '',
  title: '',
  poster: '',
  rating: '',
  review: '',
  category: '',
  streamed: false,
  releaseYear: '',
  platinumed: false,
  platform: '',
  allTimeFav: false,
  vodUrl: '',
};

const isUpcoming = (r) => (r.category || []).includes('Upcoming');

export default function AdminReviews() {
  const [reviews, setReviews] = useState([]);
  const [form, setForm] = useState(emptyReview);
  const [editingId, setEditingId] = useState(null);
  const [message, setMessage] = useState('');
  const [formOpen, setFormOpen] = useState(false);

  const load = () => apiGet('/reviews').then(setReviews);

  useEffect(() => {
    load().catch(console.error);
  }, []);

  const stats = useMemo(() => {
    const upcoming = reviews.filter(isUpcoming).length;
    return {
      total: reviews.length,
      upcoming,
      streamed: reviews.filter((r) => r.streamed).length,
      favorites: reviews.filter((r) => r.allTimeFav).length,
    };
  }, [reviews]);

  const sortedReviews = useMemo(
    () => [...reviews].sort((a, b) => b.id - a.id),
    [reviews],
  );

  const resetForm = () => {
    setForm(emptyReview);
    setEditingId(null);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setMessage('');
    const payload = {
      id: form.id ? Number(form.id) : undefined,
      title: form.title,
      poster: form.poster,
      rating: form.rating === '' ? null : Number(form.rating),
      review: form.review.split('\n\n').map((p) => p.trim()).filter(Boolean),
      category: form.category.split(',').map((c) => c.trim()).filter(Boolean),
      streamed: form.streamed,
      releaseYear: form.releaseYear ? Number(form.releaseYear) : null,
      platinumed: form.platinumed,
      platform: form.platform,
      allTimeFav: form.allTimeFav,
      vodUrl: form.vodUrl,
    };
    try {
      if (editingId) {
        await apiPut(`/reviews/${editingId}`, payload);
        setMessage('Review updated.');
      } else {
        await apiPost('/reviews', payload);
        setMessage('Review created.');
      }
      resetForm();
      setFormOpen(false);
      load();
    } catch (err) {
      setMessage(err.message);
    }
  };

  const startEdit = (r) => {
    setEditingId(r.id);
    setForm({
      id: r.id,
      title: r.title,
      poster: r.poster,
      rating: r.rating ?? '',
      review: (r.review || []).join('\n\n'),
      category: (r.category || []).join(', '),
      streamed: r.streamed,
      releaseYear: r.releaseYear ?? '',
      platinumed: r.platinumed,
      platform: r.platform || '',
      allTimeFav: r.allTimeFav,
      vodUrl: r.vodUrl || '',
    });
    setFormOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this review?')) return;
    await apiDelete(`/reviews/${id}`);
    if (editingId === id) {
      resetForm();
      setFormOpen(false);
    }
    load();
  };

  return (
    <div className="admin-panel-page">
      <div className="admin-panel-toolbar">
        <div className="admin-panel-toolbar-row">
          <div className="admin-stat-chips">
            <div className="admin-stat-chip">
              <span className="admin-stat-value">{stats.total}</span>
              <span className="admin-stat-label">Total</span>
            </div>
            <div className="admin-stat-chip admin-stat-chip--purple">
              <span className="admin-stat-value">{stats.upcoming}</span>
              <span className="admin-stat-label">Upcoming</span>
            </div>
            <div className="admin-stat-chip admin-stat-chip--green">
              <span className="admin-stat-value">{stats.streamed}</span>
              <span className="admin-stat-label">Streamed</span>
            </div>
            <div className="admin-stat-chip admin-stat-chip--gold">
              <span className="admin-stat-value">{stats.favorites}</span>
              <span className="admin-stat-label">Favorites</span>
            </div>
          </div>
          <div className="admin-panel-toolbar-actions">
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => {
                if (formOpen && !editingId) {
                  setFormOpen(false);
                } else {
                  resetForm();
                  setFormOpen(true);
                }
              }}
            >
              <i className="fas fa-plus" aria-hidden="true" />
              {formOpen && !editingId ? 'Hide' : 'Add review'}
            </button>
          </div>
        </div>
      </div>

      {formOpen && (
        <form className="admin-panel-form admin-review-form" onSubmit={handleSave}>
          <div className="admin-review-form-head">
            <h3>{editingId ? `Edit #${editingId}` : 'New review'}</h3>
            {message && <p className="admin-message">{message}</p>}
          </div>

          <div className="admin-review-form-layout">
            <aside className="admin-review-preview">
              <div className="admin-review-poster">
                {form.poster ? (
                  <img src={form.poster} alt="" />
                ) : (
                  <div className="admin-review-poster-empty">
                    <i className="fas fa-image" aria-hidden="true" />
                  </div>
                )}
                {form.rating !== '' && (
                  <span className="admin-review-rating-badge">{form.rating}/10</span>
                )}
              </div>
              <div className="admin-review-flag-row">
                <button
                  type="button"
                  className={`admin-flag-toggle ${form.streamed ? 'is-on' : ''}`}
                  onClick={() => setForm({ ...form, streamed: !form.streamed })}
                >
                  Streamed
                </button>
                <button
                  type="button"
                  className={`admin-flag-toggle ${form.platinumed ? 'is-on' : ''}`}
                  onClick={() => setForm({ ...form, platinumed: !form.platinumed })}
                >
                  Platinum
                </button>
                <button
                  type="button"
                  className={`admin-flag-toggle is-fav ${form.allTimeFav ? 'is-on' : ''}`}
                  onClick={() => setForm({ ...form, allTimeFav: !form.allTimeFav })}
                >
                  Favorite
                </button>
              </div>
            </aside>

            <div className="admin-review-fields">
              <div className="admin-review-form-grid">
                {!editingId && (
                  <label className="admin-catalog-field admin-review-field--id">
                    <span>ID</span>
                    <input
                      value={form.id}
                      onChange={(e) => setForm({ ...form, id: e.target.value })}
                      placeholder="Auto"
                    />
                  </label>
                )}
                <label className={`admin-catalog-field admin-review-field--title ${editingId ? 'admin-review-field--title-only' : ''}`}>
                  <span>Title</span>
                  <input
                    value={form.title}
                    onChange={(e) => setForm({ ...form, title: e.target.value })}
                    required
                  />
                </label>
                <label className="admin-catalog-field admin-review-field--rating">
                  <span>Rating</span>
                  <input
                    value={form.rating}
                    onChange={(e) => setForm({ ...form, rating: e.target.value })}
                    placeholder="0–10"
                  />
                </label>
                <label className="admin-catalog-field admin-review-field--year">
                  <span>Year</span>
                  <input
                    value={form.releaseYear}
                    onChange={(e) => setForm({ ...form, releaseYear: e.target.value })}
                  />
                </label>
                <label className="admin-catalog-field admin-review-field--platform">
                  <span>Platform</span>
                  <input
                    value={form.platform}
                    onChange={(e) => setForm({ ...form, platform: e.target.value })}
                    placeholder="PS5 Pro"
                  />
                </label>
                <label className="admin-catalog-field admin-review-field--categories">
                  <span>Categories</span>
                  <input
                    value={form.category}
                    onChange={(e) => setForm({ ...form, category: e.target.value })}
                    placeholder="Action, Adventure, Upcoming"
                  />
                </label>
                <div className="admin-catalog-field admin-review-field--full">
                  <MediaPicker
                    label="Poster"
                    value={form.poster}
                    onChange={(url) => setForm({ ...form, poster: url })}
                    placeholder="https://images.igdb.com/…"
                  />
                </div>
                <label className="admin-catalog-field admin-review-field--full">
                  <span>VOD URL</span>
                  <input
                    value={form.vodUrl}
                    onChange={(e) => setForm({ ...form, vodUrl: e.target.value })}
                    placeholder="https://youtube.com/…"
                  />
                </label>
                <label className="admin-catalog-field admin-review-field--full">
                  <span>Review</span>
                  <textarea
                    value={form.review}
                    onChange={(e) => setForm({ ...form, review: e.target.value })}
                    rows={3}
                    placeholder="Paragraphs separated by a blank line"
                  />
                </label>
              </div>
            </div>
          </div>

          <div className="admin-catalog-form-actions">
            {editingId && (
              <button type="button" className="btn btn-secondary" onClick={() => { resetForm(); setFormOpen(false); }}>
                Cancel
              </button>
            )}
            <button type="submit" className="btn btn-primary">
              {editingId ? 'Update review' : 'Create review'}
            </button>
          </div>
        </form>
      )}

      <div className="admin-list-wrap">
        <ul className="admin-list">
          {sortedReviews.map((r) => (
            <li key={r.id} className={`admin-list-item ${editingId === r.id ? 'is-editing' : ''}`}>
              {r.poster ? (
                <img src={r.poster} alt="" className="admin-thumb" />
              ) : (
                <span className="admin-thumb is-empty"><i className="fas fa-gamepad" aria-hidden="true" /></span>
              )}
              <div className="admin-list-main">
                <div className="admin-list-title-row">
                  <span className="admin-list-title">{r.title}</span>
                  <span className="admin-list-sub">#{r.id}</span>
                </div>
                <div className="admin-list-pills">
                  {r.rating != null && <span className="admin-pill">{r.rating}/10</span>}
                  {isUpcoming(r) && <span className="admin-pill is-purple">Upcoming</span>}
                  {r.streamed && <span className="admin-pill is-green">Streamed</span>}
                  {r.allTimeFav && <span className="admin-pill is-gold">Favorite</span>}
                  {r.platinumed && <span className="admin-pill">Platinum</span>}
                </div>
              </div>
              <div className="admin-list-actions">
                <button type="button" className="admin-icon-btn" onClick={() => startEdit(r)} title="Edit">
                  <i className="fas fa-pen" aria-hidden="true" />
                </button>
                <button type="button" className="admin-icon-btn danger" onClick={() => handleDelete(r.id)} title="Delete">
                  <i className="fas fa-trash" aria-hidden="true" />
                </button>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
